/* eslint-disable no-case-declarations */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import SelectTips from '../../../Popovers/SelectTips';
import Select from '../../../../juristec-ui/core/SelectNew';
import Button from '../../../../juristec-ui/core/Button';
import { FilterColumn } from '../types';
import { SelectOption } from '../../../../types';
import { dateOptions } from '../../../../options';
import {
  optionNamespace,
  GroupOptions,
  useKpiModifiers,
  useModifierConfig,
  TypePeriodOptions,
  type Modifier,
  type ModifierGroup,
  type Value,
} from '../../../../hooks/modifiers';
import KpiFilter from '../../../KpiFilter';
import KpiFilterApplied from '../../../KpiFilterApplied';
import {
  FiltersContainer,
  ColumnContainer,
  BadgersContainer,
  ScrollContainer,
  OptionalLabel,
} from './styled/Filters.styled';

interface FiltersProps {
  fileColumns: SelectOption[];
  getColumnData: (column: string, selector: string) => Promise<SelectOption[]>;
  filterColumns: FilterColumn[];
  setFilterColumns: (filters: FilterColumn[]) => void;
}
/**
 * Second step for the 'CopyDashboard'. This is where the filters for the columns can be applied.
 */
const Filters = ({
  fileColumns,
  getColumnData,
  filterColumns,
  setFilterColumns,
}: FiltersProps) => {
  const { t } = useTranslation('');

  const dataFrameTypes = useMemo(() => Object.fromEntries(
    fileColumns.map((col) => [col.value, col.type ?? 'object']),
  ), [fileColumns]);

  const {
    modifierConfig,
    simpleOptions,
    getColumnOptions,
    getSimpleOptions,
    handleModifierConfigs,
    resetModifierConfigs,
    //
    configColumnIsDate,
    getOptionsByConfigGroup,
    checkModifierValues,
  } = useModifierConfig({
    dataFrameTypes,
    modifierOrigins: { filter: 'Filtro' },
    getUniqueValues: getColumnData,
  });

  const handleModifierChange = (newModifiers: ModifierGroup, origin: string) => {
    setFilterColumns(newModifiers[origin] as FilterColumn[]);
  };

  const {
    modifiers,
    addModifier,
    removeModifier,
    editModifier,
    changeModifierOrder,
  } = useKpiModifiers({
    dataFrameTypes,
    initialModifiers: { filter: (filterColumns ?? []) as Modifier[] },
    onModifierChange: handleModifierChange,
  });

  const handleColumnSelect = (selected: SelectOption) => handleModifierConfigs({
    column: selected,
  });
  const handleGroupSelect = (selected: SelectOption) => handleModifierConfigs({
    group: selected,
  });
  const handleSelectorSelect = (selected: SelectOption) => handleModifierConfigs({
    selector: selected,
  });
  const handleFormatSelect = (selected: SelectOption) => handleModifierConfigs({
    format: selected,
  });
  const handleFilterChange = (values: Value[]) => handleModifierConfigs({ values });

  const handleAddModifier = () => {
    addModifier(modifierConfig);
    resetModifierConfigs();
  };

  return (
    <>
      <OptionalLabel>Não é obrigatório a utilização do filtro!</OptionalLabel>
      <FiltersContainer>
        <ColumnContainer style={{ gap: '10px' }}>
          <div>
            <Select
              label="Coluna para filtrar"
              onChange={handleColumnSelect}
              // @ts-expect-error not typed yet
              value={modifierConfig.column}
              options={getColumnOptions({ filter: fileColumns })}
              atModal
              searchable
              clearable
              sortBy="ascending"
            />
          </div>
          {(modifierConfig.column?.value?.length ?? 0) > 0 && (
            <>
              <div>
                <Select
                  label="Tipo"
                  onChange={handleGroupSelect}
                  // @ts-expect-error not typed yet
                  value={modifierConfig.group}
                  options={GroupOptions.list}
                  optionNamespace={optionNamespace}
                  atModal
                />
              </div>

              {modifierConfig.group.value === GroupOptions.enum.SIMPLE
              && configColumnIsDate() && (
                <div>
                  <Select
                    label="Formato"
                    onChange={handleSelectorSelect}
                    // @ts-expect-error not typed yet
                    value={modifierConfig.selector}
                    options={dateOptions}
                    searchable
                    atModal
                  />
                </div>
              )}
              <div style={{ position: 'relative' }}>
                <Select
                  label="Condição"
                  onChange={handleFormatSelect}
                  // @ts-expect-error not typed yet
                  value={modifierConfig.format}
                  options={getOptionsByConfigGroup()}
                  optionNamespace={optionNamespace}
                  searchable
                  atModal
                />
                {modifierConfig.format.value !== '' && (
                  <SelectTips
                    description={t(modifierConfig.format.description, {
                      ns: optionNamespace,
                      ...(modifierConfig.format.params ?? {}),
                    }) as string}
                    style={{ top: '5px', right: '5px' }}
                  />
                )}
              </div>

              {(modifierConfig.column?.value?.length ?? 0) > 0
              && modifierConfig.format?.value.length > 0
              && (
                modifierConfig.group?.value === GroupOptions.enum.ADVANCED
                || simpleOptions.length > 0
              ) && (
                <>
                  {!Object.keys(TypePeriodOptions.map).includes(modifierConfig.format.value) && (
                    <KpiFilter
                      filterType={modifierConfig.format.value}
                      // @ts-expect-error not typed yet
                      handle={handleFilterChange}
                      // @ts-expect-error not typed yet
                      values={modifierConfig.values}
                      // @ts-expect-error not typed yet
                      options={getSimpleOptions(modifiers)}
                      format={modifierConfig.column?.type}
                      atModal
                    />
                  )}

                  <Button
                    disabled={!checkModifierValues()}
                    onClick={handleAddModifier}
                    color="secondary"
                  >
                    Adicionar filtro
                  </Button>
                </>
              )}
            </>
          )}
        </ColumnContainer>
        <ColumnContainer>
          <span className="label">Filtros Aplicados:</span>
          <ScrollContainer>
            {filterColumns?.length > 0 ? (
              filterColumns.map((filterC, i) => (
                <KpiFilterApplied
                  key={filterC.uid}
                  origin="filter"
                  // @ts-expect-error not typed yet
                  modifier={filterC}
                  modifiers={modifiers}
                  removeFunc={removeModifier}
                  editFunc={editModifier}
                  getOptions={getColumnData}
                  dataFrameTypes={dataFrameTypes}
                  position={i}
                  isLast={filterColumns.length - 1 === i}
                  changePosition={changeModifierOrder}
                  atModal
                />
              ))
            ) : (
              <BadgersContainer>
                <div className="badgersHeader">
                  <span>Nenhum filtro selecionado</span>
                </div>
              </BadgersContainer>
            )}
          </ScrollContainer>
        </ColumnContainer>
      </FiltersContainer>
    </>
  );
};

export default Filters;
